import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "lucide-react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null); // null=loading, false=invalid
  const [form, setForm] = useState({ name: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setInvite(false); return; }
    api.get(`/invitations/${token}`)
      .then((r) => { setInvite(r.data); setForm((f) => ({ ...f, name: r.data.name || "" })); })
      .catch(() => setInvite(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, ...form });
      setUser(data);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-[#0A1B2E] text-white font-body flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <Link to="/" className="font-body font-bold text-2xl tracking-tight">Retain<span className="text-[#0E7C7B]">Proof</span><sup className="text-[0.5em] font-normal align-super">™</sup></Link>
        {invite === null && <p className="text-zinc-500 text-sm mt-8">Loading invitation…</p>}
        {invite === false && (
          <div className="mt-8" data-testid="invite-invalid">
            <h2 className="font-display text-3xl font-light">Invitation invalid</h2>
            <p className="text-zinc-500 text-sm mt-2">This invitation link is invalid or has already been used.</p>
            <Button asChild variant="outline" className="mt-6 rounded-none border-white/20 text-white"><Link to="/login">Go to sign in</Link></Button>
          </div>
        )}
        {invite && (
          <form onSubmit={submit} className="mt-8" data-testid="accept-invite-form">
            <h2 className="font-display text-4xl font-light">Join {invite.firm_name}</h2>
            <p className="text-zinc-500 text-sm mt-2 mb-8">
              You're joining as <span className="text-[#D5A44A]">{invite.role_label}</span> · {invite.email}
            </p>
            {error && <div className="mb-4 border border-red-500/40 bg-red-500/10 text-red-300 text-sm p-3" data-testid="accept-invite-error">{error}</div>}
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-widest font-mono-data">Your name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="invite-name-field" className="mt-2 bg-white/5 border-white/15 rounded-none text-white h-11" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-widest font-mono-data">Set a password</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="invite-password-field" className="mt-2 bg-white/5 border-white/15 rounded-none text-white h-11" />
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="accept-invite-submit" className="w-full mt-8 bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-11 font-medium group">
              {loading ? "Joining…" : "Join firm"}
              {!loading && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
